import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { FREQ_LABELS } from "../lib/format";
import { toast } from "sonner";
import { WsibProfile } from "./WsibProfile";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose,
} from "./ui/dialog";

export const EMPTY_COMPANY = { name: "", cra_business_number: "", address_line1: "", city: "", province: "ON", postal_code: "", default_pay_frequency: "biweekly", default_vacation_rate: 4, default_vacation_mode: "accrue", default_earnings: [], default_deductions: [], schedules: [], journal_accounts: {}, wsib_rates: [], wsib_filing_frequency: "quarterly", payroll_over_2_5m: false, logo: "", payroll_hours_source: "manual", payroll_due_by_days: 2 };
const PROVINCES = ["ON", "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "PE", "QC", "SK", "YT"];
const JOURNAL_ACCOUNTS = [
  ["wages", "Salaries & Wages Expense"],
  ["employer_cpp", "Employer CPP Expense"],
  ["employer_ei", "Employer EI Expense"],
  ["cpp_payable", "CPP Payable"],
  ["ei_payable", "EI Payable"],
  ["tax_payable", "Income Tax Payable (CRA)"],
  ["rpp_payable", "RPP / Pre-tax Deductions Payable"],
  ["other_deductions", "Other Deductions Payable"],
  ["net_pay", "Net Pay / Bank Clearing"],
];

export default function CompanyFormDialog({ open, onOpenChange, company, onSaved }) {
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [busy, setBusy] = useState(false);
  const editingId = company?.company_id || null;

  useEffect(() => {
    if (open) setForm(company ? { ...EMPTY_COMPANY, ...company } : EMPTY_COMPANY);
  }, [open, company]);

  const save = async () => {
    setBusy(true);
    try {
      let res;
      if (editingId) { res = await api.put(`/companies/${editingId}`, form); toast.success("Company updated"); }
      else { res = await api.post("/companies", form); toast.success("Client company created"); }
      onOpenChange(false);
      onSaved && onSaved(res?.data || form);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-black w-[96vw] sm:max-w-[1000px] max-h-[94vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="tracking-tight">{editingId ? "Edit Client Company" : "New Client Company"}</DialogTitle>
          <DialogDescription className="text-xs">Onboard a client account to manage its employees and payroll.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company name" testid="company-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="CRA Business Number" testid="company-bn-input" value={form.cra_business_number} onChange={(v) => setForm({ ...form, cra_business_number: v })} placeholder="123456789RP0001" />
          </div>
          <Field label="Address line" testid="company-address-input" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} placeholder="200 Bay St" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="City" testid="company-city-input" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="Toronto" />
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Province</label>
              <select data-testid="company-province-select" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Field label="Postal code" testid="company-postal-input" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} placeholder="M5J 2J2" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Default frequency</label>
              <select data-testid="company-frequency-select" value={form.default_pay_frequency} onChange={(e) => setForm({ ...form, default_pay_frequency: e.target.value })} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="company-funding-hint">Payroll funding (direct deposit) is set up from the company page → Direct deposit, via Forte / Agile Payments. The old EFT originator fields are retired.</p>
          <LogoField value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
          <ScheduleLines items={form.schedules} onChange={(v) => setForm({ ...form, schedules: v })} />
          <div className="border-t border-border pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Default policies (new hires inherit these)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Vacation %</label>
                <input data-testid="company-vac-rate" type="number" value={form.default_vacation_rate} onChange={(e) => setForm({ ...form, default_vacation_rate: e.target.value })} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Vacation mode</label>
                <select data-testid="company-vac-mode" value={form.default_vacation_mode} onChange={(e) => setForm({ ...form, default_vacation_mode: e.target.value })} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                  <option value="accrue">Accrue</option>
                  <option value="payout">Pay out</option>
                </select>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <PolicyLines label="Default earnings" testid="earning" items={form.default_earnings} flag="taxable" onChange={(v) => setForm({ ...form, default_earnings: v })} />
            <PolicyLines label="Default deductions" testid="deduction" items={form.default_deductions} flag="pre_tax" onChange={(v) => setForm({ ...form, default_deductions: v })} />
          </div>
          <JournalAccounts value={form.journal_accounts} onChange={(v) => setForm({ ...form, journal_accounts: v })} />
          <WsibProfile items={form.wsib_rates} onChange={(v) => setForm({ ...form, wsib_rates: v })} ratesYear={form.wsib_rates_year} onRatesYear={(y) => setForm({ ...form, wsib_rates_year: y })} />
          <div className="border-t border-border pt-3">
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">WSIB remittance filing frequency</label>
            <select data-testid="company-wsib-frequency" value={form.wsib_filing_frequency || "quarterly"} onChange={(e) => setForm({ ...form, wsib_filing_frequency: e.target.value })} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">Drives the remittance schedule &amp; due-date flags on the WSIB Reconciliation report.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer border-t border-border pt-3">
            <input data-testid="company-payroll-2-5m" type="checkbox" checked={!!form.payroll_over_2_5m} onChange={(e) => setForm({ ...form, payroll_over_2_5m: e.target.checked })} className="h-4 w-4 accent-black" />
            Annual payroll ≥ $2.5M (ESA severance applies)
          </label>
          <div className="border-t border-border pt-3">
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payroll Due By (processor deadline)</label>
            <div className="mt-1.5 flex items-center gap-2 text-sm">
              <input data-testid="company-due-by-days" type="number" min="0" max="10" value={form.payroll_due_by_days ?? 2} onChange={(e) => setForm({ ...form, payroll_due_by_days: e.target.value === "" ? "" : parseInt(e.target.value, 10) })} className="w-20 border border-input px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-black" />
              <span>business days before each pay date</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Approved time and payroll inputs are due to the payroll processor by this date. After the Due By date, payment to employees may be delayed. Each schedule can override it below.</p>
          </div>
          <div className="border-t border-border pt-3">
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payroll Hours Source</label>
            <select data-testid="company-hours-source" value={form.payroll_hours_source === "approved_time" ? "approved_time" : "manual"}
              onChange={(e) => setForm({ ...form, payroll_hours_source: e.target.value })}
              className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="approved_time">Approved PostWage Time &amp; Leave</option>
              <option value="manual">Manual Payroll Entry</option>
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">
              {form.payroll_hours_source === "approved_time"
                ? "Automatically use approved regular hours, overtime, vacation and applicable paid leave when building payroll."
                : "Enter payroll hours and earnings manually without automatically importing approved Time & Leave entries."}
            </p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <button data-testid="cancel-company-button" className="w-full sm:w-auto border border-border px-6 py-3 text-sm font-medium hover:border-black transition-colors">Cancel</button>
          </DialogClose>
          <button data-testid="save-company-button" disabled={busy || !form.name} onClick={save} className="w-full sm:w-auto sm:px-10 bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : editingId ? "Save changes" : "Create Company"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
    </div>
  );
}

function LogoField({ value, onChange }) {
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { alert("Please use an image under 400 KB."); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Company logo (appears on pay stubs & letters)</label>
      <div className="mt-1.5 flex items-center gap-3">
        {value ? (
          <img data-testid="company-logo-preview" src={value} alt="logo" className="h-12 w-auto max-w-[120px] object-contain border border-input bg-white p-1" />
        ) : (
          <div className="h-12 w-24 border border-dashed border-input flex items-center justify-center text-[10px] text-muted-foreground">No logo</div>
        )}
        <div className="flex-1 space-y-2">
          <input data-testid="company-logo-url" value={(value || "").startsWith("data:") ? "" : (value || "")} placeholder="Paste a public image URL…" onChange={(e) => onChange(e.target.value)} className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <div className="flex items-center gap-2">
            <input data-testid="company-logo-upload" type="file" accept="image/*" onChange={onFile} className="text-xs" />
            {value && <button type="button" data-testid="company-logo-clear" onClick={() => onChange("")} className="text-xs text-red-600 hover:underline">Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function JournalAccounts({ value, onChange }) {
  const v = value || {};
  const upd = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="border-t border-border pt-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Journal / QuickBooks account names</div>
      <p className="text-[10px] text-muted-foreground mb-2">Map each payroll account to your chart-of-accounts name so the run journal CSV imports with zero renaming. Leave blank to use the default.</p>
      <div className="grid sm:grid-cols-2 gap-x-4">
        {JOURNAL_ACCOUNTS.map(([key, def]) => (
          <div key={key} className="flex items-center gap-2 mb-2">
            <span className="text-[11px] text-muted-foreground w-36 shrink-0 truncate" title={def}>{def}</span>
            <input data-testid={`company-journal-${key}`} placeholder={def} value={v[key] || ""} onChange={(e) => upd(key, e.target.value)} className="flex-1 border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ScheduleLines({ items, onChange }) {
  const list = items || [];
  const add = () => onChange([...list, { name: "", frequency: "biweekly" }]);
  const upd = (i, k, v) => { const a = [...list]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Pay schedules (labels)</span>
        <button type="button" data-testid="company-add-schedule" onClick={add} className="text-xs border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors">+ Add</button>
      </div>
      <p className="text-[10px] text-muted-foreground mb-2">Label recurring runs (e.g. "Salaried bi-weekly") so drafts stay separate per schedule.</p>
      {list.map((s, i) => (
        <div key={i} data-testid={`company-schedule-${i}`} className="flex items-center gap-2 mb-2">
          <input data-testid={`company-schedule-name-${i}`} placeholder="e.g. Salaried bi-weekly" value={s.name} onChange={(e) => upd(i, "name", e.target.value)} className="flex-1 border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <select data-testid={`company-schedule-freq-${i}`} value={s.frequency} onChange={(e) => upd(i, "frequency", e.target.value)} className="border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input data-testid={`company-schedule-due-${i}`} type="number" min="0" max="10" placeholder="Due" title="Due By: business days before pay date (blank = company default)" value={s.due_by_days ?? ""} onChange={(e) => upd(i, "due_by_days", e.target.value === "" ? "" : parseInt(e.target.value, 10))} className="w-16 border border-input px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-black" />
          <button type="button" onClick={() => del(i)} className="text-xs border border-border px-2 py-1.5 hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">×</button>
        </div>
      ))}
    </div>
  );
}

function PolicyLines({ label, testid, items, flag, onChange }) {
  const list = items || [];
  const flagDefault = flag === "taxable";
  const add = () => onChange([...list, { name: "", amount: 0, [flag]: flagDefault }]);
  const upd = (i, k, v) => { const a = [...list]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
        <button type="button" data-testid={`company-add-${testid}`} onClick={add} className="text-xs border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors">+ Add</button>
      </div>
      {list.map((ln, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input placeholder="Name" value={ln.name} onChange={(e) => upd(i, "name", e.target.value)} className="flex-1 border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <input placeholder="Amt" type="number" value={ln.amount} onChange={(e) => upd(i, "amount", e.target.value)} className="w-20 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
          <label className="flex items-center gap-1 text-xs whitespace-nowrap"><input type="checkbox" checked={flag === "taxable" ? ln[flag] !== false : !!ln[flag]} onChange={(e) => upd(i, flag, e.target.checked)} className="accent-black" /> {flag === "taxable" ? "tax" : "pre"}</label>
          <button type="button" onClick={() => del(i)} className="text-xs border border-border px-2 py-1.5 hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">×</button>
        </div>
      ))}
    </div>
  );
}
